import { t } from "@travily/i18n";
import { Wizard } from "@ui/components/Wizard";
import type { ReactNode } from "react";
import { TitleField, titleFieldCopy } from "@/components/ListingFields/TitleField";

const COLOR = "green" as const;
const LISTING_TYPE = "service" as const;

export interface ServiceTitleStepViewProps {
  /** Wizard progress: 1-based index of the current stage. */
  currentStage: number;
  /** Wizard progress: total number of stages. */
  totalStages: number;
  /** Navigate to the previous stage. Omit to hide the back button. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Questions?", "Finish later"). */
  topBarActions: ReactNode;
  /** Advance to / submit the current stage. */
  onPrimary: () => void;
  /** Whether the primary button shows a pending spinner. */
  primaryPending: boolean;
  /** Current value of the title field. */
  value: string;
  /** Field error for the title input, or undefined when valid. */
  error?: string;
  /** Called with the new value on every field change. */
  onChange: (value: string) => void;
  /** Called when the field loses focus (drives blur-time validation). */
  onBlur?: () => void;
}

export function ServiceTitleStepView({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  value,
  error,
  onChange,
  onBlur,
}: ServiceTitleStepViewProps) {
  const { heading, helper } = titleFieldCopy(LISTING_TYPE);

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage heading={heading} helper={helper}>
        <div className="w-full max-w-[600px]">
          <TitleField
            error={error}
            listingType={LISTING_TYPE}
            onBlur={onBlur}
            onChange={onChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !primaryPending) onPrimary();
            }}
            value={value}
          />
        </div>
      </Wizard.Stage>
    </Wizard>
  );
}
